import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  BLOCK_KEYS,
  type BlockEnvelope,
  type BlockKey,
  type LookupStreamEvent,
  type RiskAssessment,
} from '@foco/contracts';
import { createLookup, lookupStreamUrl, fetchBlock, parseStreamEvent } from '../../shared/api/endpoints';
import { ApiError, translateApiError } from '../../shared/api/client';
import { BlockCard } from './BlockCard';
import { RiskPanel } from './RiskPanel';

/**
 * The result card.
 *
 * This screen is where the architecture has to be visible to the user, and it is
 * the reason the whole streaming design exists:
 *
 *   * **Every block is rendered immediately as a skeleton**, before any data has
 *     arrived, so the page has shape within milliseconds and the user can see
 *     what is being checked rather than staring at one global spinner.
 *   * **Blocks fill in independently** as their SSE events land. A slow registry
 *     delays its own block and nothing else.
 *   * **A failed block does not break the card.** It renders inline as "source
 *     did not respond" with a retry for that block alone (section 4.6).
 *   * **The stream falls back to polling.** If SSE cannot connect — a proxy that
 *     strips `text/event-stream`, a corporate firewall, a suspended tab — the
 *     card polls each block instead. The user never sees an empty page because
 *     of a transport detail.
 *
 * Closed data never reaches this component: the server withholds it and sends a
 * mask flag, so there is nothing in the browser to reveal.
 */

type BlockMap = Partial<Record<BlockKey, BlockEnvelope>>;

const POLL_INTERVAL_MS = 1500;
/** Give the stream a moment before deciding it is not going to connect. */
const STREAM_GRACE_MS = 6000;

export function LookupPage(): React.ReactNode {
  const { t } = useTranslation();
  // The id and the original query come from the URL, so a reload during a check
  // resumes the same lookup instead of starting a new one — section 23.3 requires
  // exactly that.
  const params = useParams<{ lookupId: string }>();
  const [search] = useSearchParams();
  const lookupId = params.lookupId ?? '';
  const initialQuery = search.get('q') ?? undefined;

  const [blocks, setBlocks] = useState<BlockMap>({});
  const [risk, setRisk] = useState<RiskAssessment | null>(null);
  const [status, setStatus] = useState<'streaming' | 'polling' | 'done' | 'error'>('streaming');
  const [error, setError] = useState<{ message: string; code: string; notFound: boolean } | null>(null);
  const [retrying, setRetrying] = useState<BlockKey | null>(null);

  // Whether the fallback has taken over; a ref so the effect does not re-run.
  const pollingRef = useRef(false);

  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;

  /* ---------------------------------------------------------------- *
   * Stream, with a polling fallback
   * ---------------------------------------------------------------- */

  useEffect(() => {
    let cancelled = false;
    let source: EventSource | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    let receivedAny = false;

    const applyEvent = (event: LookupStreamEvent) => {
      if (cancelled) return;
      receivedAny = true;

      if (event.type === 'block') {
        setBlocks((current) => ({ ...current, [event.block.key]: event.block }));
        return;
      }
      if (event.type === 'risk') {
        setRisk(event.risk);
        return;
      }
      if (event.type === 'done') {
        setStatus('done');
        stopPolling();
        return;
      }
      setError({ message: event.message, code: event.code, notFound: event.code === 'not_found' });
      setStatus('error');
    };

    /* -- polling fallback -- */
    const pollOnce = async () => {
      // Only the blocks still unresolved are polled: re-fetching a finished
      // block would spend a request on data already on screen.
      const pending = BLOCK_KEYS.filter((key) => {
        const block = blocksRef.current[key];
        return !block || block.state === 'pending';
      });

      if (pending.length === 0) {
        stopPolling();
        setStatus('done');
        return;
      }

      await Promise.all(
        pending.map(async (key) => {
          try {
            const response = await fetchBlock(lookupId, key);
            if (cancelled) return;
            const block = (response as unknown as { block: BlockEnvelope | null }).block;
            if (block) setBlocks((current) => ({ ...current, [key]: block }));
          } catch {
            // A failed poll for one block is expected while its source is still
            // working; the next tick tries again.
          }
        }),
      );
    };

    const startPolling = () => {
      if (pollingRef.current || cancelled) return;
      pollingRef.current = true;
      setStatus('polling');
      void pollOnce();
      pollTimer = setInterval(() => void pollOnce(), POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    /* -- SSE -- */
    try {
      source = new EventSource(lookupStreamUrl(lookupId));

      source.onmessage = (message) => {
        const event = parseStreamEvent(message.data);
        if (!event) return;
        // Any event at all proves the transport works, so the fallback timer is
        // cancelled even if the first event is only a keep-alive-ish block.
        if (graceTimer) {
          clearTimeout(graceTimer);
          graceTimer = null;
        }
        applyEvent(event);
      };

      source.onerror = () => {
        // `EventSource` reconnects on its own, so an error here is not
        // necessarily fatal. If nothing has arrived after the grace period
        // though, the endpoint is not wired for streaming and polling takes
        // over rather than leaving the user with permanent skeletons.
        if (!receivedAny) return;
        source?.close();
        startPolling();
      };

      graceTimer = setTimeout(() => {
        if (!receivedAny && !cancelled) {
          // Never delivered a single event: assume the stream is not usable.
          source?.close();
          startPolling();
        }
      }, STREAM_GRACE_MS);
    } catch {
      // `EventSource` can throw on a malformed URL; polling still works.
      startPolling();
    }

    return () => {
      cancelled = true;
      source?.close();
      stopPolling();
      if (graceTimer) clearTimeout(graceTimer);
      pollingRef.current = false;
    };
  }, [lookupId]);

  /* ---------------------------------------------------------------- *
   * Retry a single block (section 23.3)
   * ---------------------------------------------------------------- */

  const retryBlock = async (key: BlockKey) => {
    setRetrying(key);
    try {
      // The retry re-reads one block from the server, which will re-ask only the
      // sources for that block. Nothing else on the card is disturbed.
      const response = await fetchBlock(lookupId, key);
      const block = (response as unknown as { block: BlockEnvelope | null }).block;
      if (block) setBlocks((current) => ({ ...current, [key]: block }));
    } catch (cause) {
      const message = translateApiError(cause, t);
      setBlocks((current) => ({
        ...current,
        [key]: {
          key,
          label: {},
          state: 'unavailable',
          data: null,
          sources: [],
          latencyMs: null,
          error: { code: 'retry_failed', message, retryable: true },
        },
      }));
    } finally {
      setRetrying(null);
    }
  };

  const readyCount = useMemo(
    () => Object.values(blocks).filter((block) => block?.state === 'ready').length,
    [blocks],
  );

  const orderedBlocks = useMemo(
    () => BLOCK_KEYS.map((key) => ({ key, block: blocks[key] })),
    [blocks],
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6">
        <h1 className="text-xl font-bold sm:text-2xl">{t('lookup.title')}</h1>
        {initialQuery && (
          <p className="mt-1 text-sm text-[var(--color-muted-fg)]">
            {t('lookup.query')}: <span className="font-mono">{initialQuery}</span>
          </p>
        )}

        <p className="mt-2 text-sm text-[var(--color-muted-fg)]" aria-live="polite">
          {status === 'done'
            ? t('lookup.blocksReady', { ready: readyCount, total: BLOCK_KEYS.length })
            : t('lookup.progress', { ready: readyCount, total: BLOCK_KEYS.length })}
          {status === 'polling' && ` · ${t('lookup.streamFallback')}`}
        </p>
      </header>

      {error && (
        <div
          role="alert"
          className="mb-6 rounded-[var(--radius-card)] border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100"
        >
          <p className="font-semibold">
            {error.notFound ? t('lookup.notFound.title') : t('errors.generic')}
          </p>
          <p className="mt-1">
            {t(`lookup_errors.${error.code}`, { defaultValue: error.message })}
          </p>
        </div>
      )}

      {risk && <RiskPanel risk={risk} />}

      <div className="grid gap-4 lg:grid-cols-2">
        {orderedBlocks.map(({ key, block }) => (
          <BlockCard
            key={key}
            blockKey={key}
            block={block}
            onRetry={() => void retryBlock(key)}
            retrying={retrying === key}
          />
        ))}
      </div>
    </div>
  );
}
